/**
 * Pagination Preferences Management
 *
 * Centralized configuration and utilities for persisting pagination state
 * (page size preferences) across sessions using localStorage.
 *
 * Features:
 * - Single source of truth for page size options
 * - Centralized storage key registry
 * - Validation of stored values
 * - Preservation of pagination state during Redux resets
 *
 * Usage:
 * ```
 * import {
 *   PAGINATION_STORAGE_KEYS,
 *   getStoredItemsPerPage,
 *   persistItemsPerPage
 * } from 'datastage/utils/paginationPreferences';
 *
 * const itemsPerPage = getStoredItemsPerPage(PAGINATION_STORAGE_KEYS.users);
 * persistItemsPerPage(PAGINATION_STORAGE_KEYS.users, 50);
 * ```
 */

import { safeGetJSON, safeSetJSON } from './safeLocalStorage.js';

/**
 * Available page size options across the application
 * @type {number[]}
 */
export const DEFAULT_PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

/**
 * Default items per page when no preference is stored
 * Matches current Datastage default
 * @type {number}
 */
export const DEFAULT_ITEMS_PER_PAGE = 20;

/**
 * Registry of localStorage keys for pagination preferences
 * Add new entries here when implementing pagination on new pages
 *
 * @type {Object<string, string>}
 */
export const PAGINATION_STORAGE_KEYS = {
    users: 'usersItemsPerPage',
    products: 'productsItemsPerPage',
    jobs: 'jobsItemsPerPage',
    rights: 'rightsItemsPerPage',
    dateMath: 'dateMathItemsPerPage',
    pmxDeliveries: 'pmxDeliveriesItemsPerPage',
    productGate: 'productGateItemsPerPage',
    dealVersion: 'dealVersionItemsPerPage',
    dealDROChanges: 'dealDROChangesItemsPerPage',
};

/**
 * Retrieves a persisted page size for a specific component.
 * Validates the stored value against allowed options.
 *
 * @param {string} storageKey - localStorage key for this component
 * @param {number} [fallback=DEFAULT_ITEMS_PER_PAGE] - Fallback page size
 * @returns {number} Valid page size from storage or fallback
 */
export function getStoredItemsPerPage(storageKey, fallback = DEFAULT_ITEMS_PER_PAGE) {
    const saved = safeGetJSON(storageKey, null);

    if (saved === null) {
        return fallback;
    }

    // Validate against allowed options
    return DEFAULT_PAGE_SIZE_OPTIONS.includes(saved) ? saved : fallback;
}

/**
 * Persists a page size preference for a specific component.
 * Only stores values that are in the allowed options list.
 *
 * @param {string} storageKey - localStorage key for this component
 * @param {number} value - Page size to store
 * @returns {boolean} true if successfully persisted, false if invalid value
 */
export function persistItemsPerPage(storageKey, value) {
    if (!DEFAULT_PAGE_SIZE_OPTIONS.includes(value)) {
        console.warn(`Invalid page size ${value}. Must be one of:`, DEFAULT_PAGE_SIZE_OPTIONS);
        return false;
    }
    return safeSetJSON(storageKey, value);
}

/**
 * Returns the default zero-based pagination range tuple for a page size.
 * Used to initialize range when page size changes.
 *
 * @param {number} itemsPerPage - Items per page
 * @returns {[number, number]} Range tuple [startIndex, endIndex]
 *
 * @example
 * getInitialPaginationRange(20) // Returns [0, 19]
 * getInitialPaginationRange(50) // Returns [0, 49]
 */
export function getInitialPaginationRange(itemsPerPage) {
    return [0, itemsPerPage - 1];
}

/**
 * Preserves component-specific pagination preferences when resetting slice state.
 * Useful for maintaining user preferences during filter resets or state clears.
 *
 * @param {Object} state - Current slice state (contains current itemsPerPage)
 * @param {Object} nextState - Reset state being returned
 * @param {string[]} [rangeKeys=[]] - Range fields to recalculate (e.g., ['userRange'])
 * @param {string[]} [fields=['itemsPerPage']] - State fields to preserve
 * @returns {Object} Next state with preserved pagination preferences
 *
 * @example
 * // In reducer CLEAR_USERS case:
 * return withPreservedPagination(state, initialState, ['userRange']);
 * // Result: itemsPerPage preserved, userRange reset to [0, itemsPerPage-1]
 */
export function withPreservedPagination(
    state,
    nextState,
    rangeKeys = [],
    fields = ['itemsPerPage']
) {
    const preserved = {};

    // Preserve specified fields from current state
    fields.forEach((field) => {
        if (state[field] !== undefined) {
            preserved[field] = state[field];
        }
    });

    // Recalculate ranges based on preserved itemsPerPage
    const rangeItemsPerPage = preserved.itemsPerPage ?? state.itemsPerPage;
    if (rangeItemsPerPage && rangeKeys.length > 0) {
        const range = getInitialPaginationRange(rangeItemsPerPage);
        rangeKeys.forEach((key) => {
            preserved[key] = range;
        });

        // Also reset to first page
        if ('currentPage' in nextState) {
            preserved.currentPage = 0;
        }

        // Handle defaultFilterRange if it exists
        if ('defaultFilterRange' in nextState) {
            preserved.defaultFilterRange = range;
        }
    }

    return { ...nextState, ...preserved };
}
